import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Phone, Mail, MapPin, MessageCircle, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useData } from '../context/DataContext';

const Projects = () => {
  const navigate = useNavigate();
  const { data, loading } = useData();
  const [activeFilter, setActiveFilter] = useState('Tümü');

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900 text-white">
        <p>Veri yüklenemedi. Lütfen sayfayı yenileyin.</p>
      </div>
    );
  }

  const settings = data.settings || {};
  const projects = data.projects || [];

  const categories = ['Tümü', ...new Set(projects.map(p => p.category).filter(Boolean))];

  const filteredProjects = activeFilter === 'Tümü'
    ? projects
    : projects.filter(project => project.category === activeFilter);

  const handleWhatsApp = () => {
    window.open(`https://wa.me/${settings?.whatsapp || '905393254847'}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-slate-900/95 backdrop-blur-md border-b border-slate-800">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img
                src={settings?.white_logo_url || "https://baskentelektrik.net/baskentelektrik/assets/img/logo/white-logo.png"}
                alt={settings?.site_title || "Başkent Enerji"}
                className="h-12 object-contain cursor-pointer"
                onClick={() => navigate('/')}
              />
            </div>
            <nav className="hidden md:flex items-center space-x-8">
              <a href="/#anasayfa" className="text-slate-300 hover:text-amber-500 transition-colors duration-300 font-medium">Ana Sayfa</a>
              <a href="/#hakkimizda" className="text-slate-300 hover:text-amber-500 transition-colors duration-300 font-medium">Hakkımızda</a>
              <a href="/#hizmetler" className="text-slate-300 hover:text-amber-500 transition-colors duration-300 font-medium">Hizmetler</a>
              <a href="/projeler" className="text-amber-500 font-medium">Projeler</a>
              <a href="/#iletisim" className="text-slate-300 hover:text-amber-500 transition-colors duration-300 font-medium">İletişim</a>
            </nav>
            <Button onClick={handleWhatsApp} className="bg-amber-600 hover:bg-amber-700 text-white">
              <Phone className="w-4 h-4 mr-2" />
              İletişim
            </Button>
          </div>
        </div>
      </header>

      {/* WhatsApp Floating Button */}
      <button
        onClick={handleWhatsApp}
        className="fixed bottom-6 right-6 z-50 bg-green-500 hover:bg-green-600 text-white p-4 rounded-full shadow-2xl transition-all duration-300 hover:scale-110 flex items-center justify-center group"
        aria-label="WhatsApp ile iletişime geç"
      >
        <MessageCircle className="w-6 h-6" />
        <span className="absolute right-full mr-3 bg-slate-900 text-white px-3 py-2 rounded-lg text-sm whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          WhatsApp ile yazın
        </span>
      </button>

      {/* Page Header */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="container mx-auto px-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-slate-300 hover:text-white mb-8"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Ana Sayfaya Dön
          </Button>
          <div className="text-center">
            <Badge className="mb-4 bg-amber-600/20 text-amber-400 border-amber-600/50 px-4 py-2">
              Referanslarımız
            </Badge>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Başarılı Projelerimiz
            </h1>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Türkiye genelinde gerçekleştirdiğimiz enerji, elektrik ve güvenlik sistemleri projelerimiz
            </p>
          </div>
        </div>
      </section>

      {/* Filter Buttons */}
      <section className="py-8 bg-white border-b border-slate-200 sticky top-20 z-40">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((category) => (
              <Button
                key={category}
                onClick={() => setActiveFilter(category)}
                variant={activeFilter === category ? 'default' : 'outline'}
                className={`transition-all duration-300 ${activeFilter === category
                  ? 'bg-amber-600 hover:bg-amber-700 text-white'
                  : 'border-slate-300 text-slate-700 hover:border-amber-600 hover:text-amber-600'
                  }`}
              >
                {category}
              </Button>
            ))}
          </div>
          <div className="text-center mt-4">
            <p className="text-slate-600">
              <span className="font-bold text-amber-600">{filteredProjects.length}</span> proje gösteriliyor
            </p>
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProjects.map((project) => (
              <Card
                key={project.id}
                className="group hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 overflow-hidden border-slate-200"
              >

                <CardHeader>
                  <div className="mb-2">
                    <Badge className="bg-amber-100 text-amber-600 hover:bg-amber-200 border-none">
                      {project.category}
                    </Badge>
                  </div>
                  <CardTitle className="text-xl text-slate-900 group-hover:text-amber-600 transition-colors duration-300 leading-tight">
                    {project.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-slate-600 leading-relaxed" dangerouslySetInnerHTML={{ __html: project.description || '' }}>
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Siz de Projeleriniz İçin Bizimle Çalışın
          </h2>
          <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
            Tecrübeli kadromuz ve kaliteli hizmet anlayışımızla projenize değer katalım
          </p>
          <Button
            onClick={handleWhatsApp}
            size="lg"
            className="bg-amber-600 hover:bg-amber-700 text-white text-lg px-8 py-6 rounded-lg shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105"
          >
            Fiyat Teklifi Alın
            <Phone className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 text-slate-400 py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <img
                src={settings?.white_logo_url || "https://baskentelektrik.net/baskentelektrik/assets/img/logo/white-logo.png"}
                alt={settings?.site_title || "Başkent Enerji"}
                className="h-12 mb-4"
              />
              <p className="text-sm">
                {settings?.site_description || "Enerji ve savunma sistemlerinde güvenilir çözüm ortağınız."}
              </p>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Hızlı Linkler</h4>
              <ul className="space-y-2">
                <li><a href="/#anasayfa" className="hover:text-amber-400 transition-colors">Ana Sayfa</a></li>
                <li><a href="/#hakkimizda" className="hover:text-amber-400 transition-colors">Hakkımızda</a></li>
                <li><a href="/#hizmetler" className="hover:text-amber-400 transition-colors">Hizmetler</a></li>
                <li><a href="/projeler" className="hover:text-amber-400 transition-colors">Projeler</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Hizmetler</h4>
              <ul className="space-y-2">
                <li><a href="/#hizmetler" className="hover:text-amber-400 transition-colors">Elektrik Tesisatı</a></li>
                <li><a href="/#hizmetler" className="hover:text-amber-400 transition-colors">Güvenlik Sistemleri</a></li>
                <li><a href="/#hizmetler" className="hover:text-amber-400 transition-colors">Güneş Enerjisi</a></li>
                <li><a href="/#hizmetler" className="hover:text-amber-400 transition-colors">Tüm Hizmetler</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">İletişim</h4>
              <ul className="space-y-2">
                <li className="flex items-start space-x-2">
                  <Phone className="w-4 h-4 mt-1 flex-shrink-0" />
                  <span>{settings?.phone}</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Mail className="w-4 h-4 mt-1 flex-shrink-0" />
                  <span className="break-all">{settings?.email}</span>
                </li>
                <li className="flex items-start space-x-2">
                  <MapPin className="w-4 h-4 mt-1 flex-shrink-0" />
                  <span>{settings?.address}</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8 text-center">
            <p className="text-sm">
              © {new Date().getFullYear()} {settings?.site_title || "Başkent Enerji"}. Tüm hakları saklıdır.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Projects;
